import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Select from '../../components/bootstrap/forms/Select';
import Input from '../../components/bootstrap/forms/Input';
import Card, {
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
	CardActions,
} from '../../components/bootstrap/Card';
import Button from '../../components/bootstrap/Button';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import { selectLoginResponse } from '../../redux/login/login.selector';
import { getManagementLeaveReportStart } from '../../redux/NewReport/NewReport.actions';
import { selectManagementLeaveReportResponse } from '../../redux/NewReport/NewReport.selectors';
import Icon from '../../components/icon/Icon';
import TableSpinner from '../../common/table/TableSpinner';

interface LeaveReportRow {
	sNo: number;
	employeeName: string;
	employeeCode: string;
	Designation: string;
	branchName: string;
	cl: string | number;
	pl: string | number;
	plMedical: string | number;
	sl: string | number;
	total: string | number;
}

interface MonthOption {
	value: number;
	text: string;
}

interface SortConfig {
	key: keyof LeaveReportRow | null;
	direction: 'asc' | 'desc';
}

const ManagementLeaveReport = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const loginData = useSelector(selectLoginResponse);
    const reportData = useSelector(selectManagementLeaveReportResponse);
	const [month, setMonth] = useState(new Date().getMonth() + 1); // Current month
	const [year, setYear] = useState(2025); // Fixed to 2025
	const [employeeType, setEmployeeType] = useState(0); // Default to permanent employees
	const [designationFilter, setDesignationFilter] = useState('All');
	const [employeeNameFilter, setEmployeeNameFilter] = useState<string>('');
	const [employeeCodeFilter, setEmployeeCodeFilter] = useState<string>('');
	const [viewMode, setViewMode] = useState<'monthly' | 'yearly'>('monthly'); // Toggle between monthly and yearly
	const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'asc' });

	useEffect(() => {
		if (!loginData?.organisationID) {
			navigate('/auth-pages/login');
			return;
		}
	}, [loginData, navigate]);

	const months: MonthOption[] = [
		{ value: 1, text: 'January' },
		{ value: 2, text: 'February' },
		{ value: 3, text: 'March' },
		{ value: 4, text: 'April' },
		{ value: 5, text: 'May' },
		{ value: 6, text: 'June' },
		{ value: 7, text: 'July' },
		{ value: 8, text: 'August' },
		{ value: 9, text: 'September' },
		{ value: 10, text: 'October' },
		{ value: 11, text: 'November' },
		{ value: 12, text: 'December' },
	];

	// Only show 2025 for frontend
	const yearOptions: MonthOption[] = [{ value: 2025, text: '2025' }];

	const employeeTypes: MonthOption[] = [
		{ value: 0, text: 'Permanent' },
		{ value: 1, text: 'Temporary' },
	];

	useEffect(() => {
		if (loginData?.organisationID) {
			// For yearly view, send year-01 format, for monthly view send the actual month
			const selectedMonth = viewMode === 'yearly' ? `${year}-01` : month;
            dispatch(
                getManagementLeaveReportStart({
					selectedMonth: selectedMonth,
					selectedYear: year,
					organisationID: loginData.organisationID,
					employeeType: employeeType,
				}),
			);
		}
	}, [month, year, viewMode, employeeType, loginData?.organisationID, dispatch]);

	const handleSort = (key: keyof LeaveReportRow) => {
		setSortConfig((prevConfig) => ({
			key,
			direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
		}));
	};

	const getEmployeeCodePriority = (empID: string): number => {
		// Define priority based on employee code patterns
		const code = parseInt(empID);
		if (code >= 700 && code <= 799) return 1; // Senior management
		if (code >= 800 && code <= 899) return 2; // Middle management
		if (code >= 900 && code <= 999) return 3; // Junior staff
		return 4; // Others
	};

	const getDesignationPriority = (designation: string): number => {
		const priorities: { [key: string]: number } = {
			'Deputy General Manager': 1,
			'Assistant General Manager': 2,
			'IT Specialist': 3,
			'PA to Executive': 4,
			'Chief Manager': 5,
			Manager: 6,
			'Assistant Manager': 7,
			Assistant: 8,
			'System Admin': 9,
			Teller: 10,
			'Sub Staff': 11,
			Sweeper: 12,
			Intern: 13,
		};
		return priorities[designation] || 14;
	};

	const sortData = (data: LeaveReportRow[]) => {
		if (!sortConfig.key) return data;

		return [...data].sort((a, b) => {
			const aValue = a[sortConfig.key!];
			const bValue = b[sortConfig.key!];

			// Special handling for employee code sorting by hierarchy
			if (sortConfig.key === 'employeeCode') {
				const aPriority = getEmployeeCodePriority(aValue.toString());
				const bPriority = getEmployeeCodePriority(bValue.toString());

				// First sort by seniority (priority)
				if (aPriority !== bPriority) {
					return sortConfig.direction === 'asc'
						? aPriority - bPriority
						: bPriority - aPriority;
				}

				// Within same seniority level, sort by employee code numerically
				const aCode = parseInt(aValue.toString());
				const bCode = parseInt(bValue.toString());

				if (aCode !== bCode) {
					return sortConfig.direction === 'asc' ? aCode - bCode : bCode - aCode;
				}

				// If codes are the same, sort by employee name
				const aName = a.employeeName.toString();
				const bName = b.employeeName.toString();

				if (sortConfig.direction === 'asc') {
					return aName.localeCompare(bName);
				} else {
					return bName.localeCompare(aName);
				}
			}

			// Special handling for designation sorting by hierarchy
			if (sortConfig.key === 'Designation') {
				const aPriority = getDesignationPriority(aValue.toString());
				const bPriority = getDesignationPriority(bValue.toString());

				// First sort by designation priority
				if (aPriority !== bPriority) {
					return sortConfig.direction === 'asc'
						? aPriority - bPriority
						: bPriority - aPriority;
				}

				// Within same designation level, sort by designation name alphabetically
				const aDesignation = aValue.toString();
				const bDesignation = bValue.toString();

				if (sortConfig.direction === 'asc') {
					return aDesignation.localeCompare(bDesignation);
				} else {
					return bDesignation.localeCompare(aDesignation);
				}
			}

			// For numeric values, sort numerically
			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
			}

			// For string values, sort alphabetically
			if (typeof aValue === 'string' && typeof bValue === 'string') {
				return sortConfig.direction === 'asc'
					? aValue.localeCompare(bValue)
					: bValue.localeCompare(aValue);
			}

			return 0;
		});
	};

	const handleExport = () => {
		if (!reportData) return;

		const headers = [
			'S.No',
			'Employee Name',
			'Employee Code',
			'Designation',
			'Branch Name',
			'CL',
			'PL',
			'PL(Medical)',
			'SL',
			'Total',
		];
		const csvData = [
			headers,
			...filteredData.map((row: LeaveReportRow) => [
				row.sNo,
				row.employeeName,
				row.employeeCode,
				row.Designation,
				row.branchName,
				row.cl,
				row.pl,
				row.plMedical,
				row.sl,
				row.total,
			]),
		];

		const csvContent = csvData.map((row) => row.join(',')).join('\n');
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		const employeeTypeText =
			employeeTypes.find((et) => et.value === employeeType)?.text || 'Unknown';
		const fileName =
			viewMode === 'yearly'
				? `Management_Leave_Report_${year}_Yearly_${employeeTypeText}.csv`
				: `Management_Leave_Report_${months.find((m) => m.value === month)?.text}_${year}_${employeeTypeText}.csv`;
		link.setAttribute('download', fileName);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

    const loading = reportData == null;
    const data = Array.isArray(reportData?.data) ? reportData.data : [];

	// Extract unique designations for dropdown
    const uniqueDesignations = Array.from(
        new Set(data.map((row: LeaveReportRow) => row.Designation)),
    ) as string[];

    const designationOptions = [
		{ value: 'All', text: 'All' },
        ...uniqueDesignations
            .filter((d: string) => d && d !== '')
            .map((d: string) => ({ value: d, text: d })),
	];

	// Filter data by designation, employee name, and employee code
	const filteredData = data.filter((row: LeaveReportRow) => {
		// Filter by designation
		if (designationFilter !== 'All' && row.Designation !== designationFilter) {
			return false;
		}

		// Filter by employee name
		if (
			employeeNameFilter &&
			!row.employeeName.toLowerCase().includes(employeeNameFilter.toLowerCase())
		) {
			return false;
		}

		// Filter by employee code
		if (
			employeeCodeFilter &&
			!row.employeeCode.toLowerCase().includes(employeeCodeFilter.toLowerCase())
		) {
			return false;
		}

		return true;
	});

	// Apply sorting to filtered data
	const sortedData = sortData(filteredData);

	return (
		<PageWrapper>
			<SubHeader>
				<SubHeaderLeft>
					<Icon icon='Report' size='2x' color='primary' />
					<span className='h4 mb-0 ms-2'>Management Leave Report</span>
				</SubHeaderLeft>
			</SubHeader>
			<Page container='fluid'>
				<div className='row'>
					<div className='col-12'>
						<Card stretch>
							<CardHeader>
								<CardLabel icon='FilterList'>
									<CardTitle>Report Filters</CardTitle>
								</CardLabel>
								<CardActions>
									<Button
										color='primary'
										icon='Download'
										onClick={handleExport}
										isDisable={loading || data.length === 0}>
										Export Report
									</Button>
								</CardActions>
							</CardHeader>
							<CardBody>
								<div className='row g-4 mb-4'>
									<div className='col-md-2'>
										<label htmlFor='month' className='form-label fw-bold'>
											Month
										</label>
										<Select
											id='month'
											value={month.toString()}
											onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
												setMonth(Number(e.target.value))
											}
											list={months}
											ariaLabel='Select Month'
											disabled={viewMode === 'yearly'}
										/>
									</div>
									<div className='col-md-2'>
										<label htmlFor='year' className='form-label fw-bold'>
											Year
										</label>
										<Select
											id='year'
											value={year.toString()}
											onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
												setYear(Number(e.target.value))
											}
											list={yearOptions}
											ariaLabel='Select Year'
										/>
									</div>
									<div className='col-md-2'>
										<label
											htmlFor='employeeType'
											className='form-label fw-bold'>
											Employee Type
										</label>
										<Select
											id='employeeType'
											value={employeeType.toString()}
											onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
												setEmployeeType(Number(e.target.value))
											}
											list={employeeTypes}
											ariaLabel='Select Employee Type'
										/>
									</div>
									<div className='col-md-3'>
										<label htmlFor='designation' className='form-label fw-bold'>
											Designation
										</label>
										<Select
											id='designation'
											value={designationFilter}
											onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
												setDesignationFilter(e.target.value)
											}
											list={designationOptions}
											ariaLabel='Select Designation'
										/>
									</div>
									<div className='col-md-3'>
										<label className='form-label fw-bold'>View Mode</label>
										<div className='btn-group' role='group'>
											<Button
												color={viewMode === 'monthly' ? 'primary' : 'light'}
												onClick={() => setViewMode('monthly')}
												className='me-2'>
												Monthly View
											</Button>
											<Button
												color={viewMode === 'yearly' ? 'primary' : 'light'}
												onClick={() => setViewMode('yearly')}>
												Yearly View
											</Button>
										</div>
									</div>
								</div>

								{loading ? (
									<TableSpinner />
								) : filteredData.length === 0 ? (
									<div className='alert alert-info'>
										No data available for{' '}
										{viewMode === 'yearly'
											? `${year}`
											: `${months.find((m) => m.value === month)?.text} ${year}`}{' '}
										{designationFilter !== 'All'
											? `/ ${designationFilter}`
											: ''}
									</div>
								) : (
									<div
										className='table-responsive'
										style={{ maxHeight: '70vh', overflow: 'auto' }}>
										<table
											className='table table-modern table-hover'
											style={{ minWidth: '100%' }}>
											<thead
												className='table-light'
												style={{ position: 'sticky', top: 0, zIndex: 2 }}>
												<tr>
													<th
														style={{
															position: 'sticky',
															left: 0,
															backgroundColor: '#f8f9fa',
															zIndex: 3,
															width: '5%',
															borderBottom: '2px solid #dee2e6',
															minWidth: '80px',
														}}
														className='text-center fw-bold'>
														S.No
													</th>
													<th
														style={{
															position: 'sticky',
															left: '80px',
															backgroundColor: '#f8f9fa',
															zIndex: 3,
															width: '20%',
															borderBottom: '2px solid #dee2e6',
															minWidth: '200px',
															cursor: 'pointer',
														}}
														onClick={() => handleSort('employeeName')}
														className={`${sortConfig.key === 'employeeName' ? 'sortable-header' : ''}`}>
														<div className='d-flex flex-column'>
															<span className='fw-bold mb-1'>
																Employee Name
																{sortConfig.key ===
																	'employeeName' && (
																	<span className='ms-1'>
																		{sortConfig.direction ===
																		'asc'
																			? '↑'
																			: '↓'}
																	</span>
																)}
															</span>
															<Input
																type='text'
																placeholder='Filter...'
																size='sm'
																className='form-control-sm'
																onChange={(
																	e: React.ChangeEvent<HTMLInputElement>,
																) =>
																	setEmployeeNameFilter(
																		e.target.value,
																	)
																}
																value={employeeNameFilter}
																onClick={(e) => e.stopPropagation()}
															/>
														</div>
													</th>
													<th
														style={{
															width: '12%',
															borderBottom: '2px solid #dee2e6',
															minWidth: '150px',
															cursor: 'pointer',
														}}
														onClick={() => handleSort('employeeCode')}
														className={`${sortConfig.key === 'employeeCode' ? 'sortable-header' : ''}`}>
														<div className='d-flex flex-column'>
															<span className='fw-bold mb-1'>
																Employee Code
																{sortConfig.key ===
																	'employeeCode' && (
																	<span className='ms-1'>
																		{sortConfig.direction ===
																		'asc'
																			? '↑'
																			: '↓'}
																	</span>
																)}
															</span>
															<Input
																type='text'
																placeholder='Filter...'
																size='sm'
																className='form-control-sm'
																onChange={(
																	e: React.ChangeEvent<HTMLInputElement>,
																) =>
																	setEmployeeCodeFilter(
																		e.target.value,
																	)
																}
																value={employeeCodeFilter}
																onClick={(e) => e.stopPropagation()}
															/>
														</div>
													</th>
													<th
														style={{
															width: '15%',
															borderBottom: '2px solid #dee2e6',
															minWidth: '180px',
															cursor: 'pointer',
														}}
														onClick={() => handleSort('Designation')}
														className={`fw-bold ${sortConfig.key === 'Designation' ? 'sortable-header' : ''}`}>
														Designation
														{sortConfig.key === 'Designation' && (
															<span className='ms-1'>
																{sortConfig.direction === 'asc'
																	? '↑'
																	: '↓'}
															</span>
														)}
													</th>
													<th
														style={{
															width: '15%',
															borderBottom: '2px solid #dee2e6',
															minWidth: '180px',
															cursor: 'pointer',
														}}
														onClick={() => handleSort('branchName')}
														className={`fw-bold ${sortConfig.key === 'branchName' ? 'sortable-header' : ''}`}>
														Branch Name
														{sortConfig.key === 'branchName' && (
															<span className='ms-1'>
																{sortConfig.direction === 'asc'
																	? '↑'
																	: '↓'}
															</span>
														)}
													</th>
													<th
														style={{
															width: '7%',
															borderBottom: '2px solid #dee2e6',
															backgroundColor: '#e3f2fd',
															minWidth: '80px',
															cursor: 'pointer',
														}}
														onClick={() => handleSort('cl')}
														className={`text-center fw-bold ${sortConfig.key === 'cl' ? 'sortable-header' : ''}`}>
														CL
														{sortConfig.key === 'cl' && (
															<span className='ms-1'>
																{sortConfig.direction === 'asc'
																	? '↑'
																	: '↓'}
															</span>
														)}
													</th>
													<th
														style={{
															width: '7%',
															borderBottom: '2px solid #dee2e6',
															backgroundColor: '#e3f2fd',
															minWidth: '80px',
															cursor: 'pointer',
														}}
														onClick={() => handleSort('pl')}
														className={`text-center fw-bold ${sortConfig.key === 'pl' ? 'sortable-header' : ''}`}>
														PL
														{sortConfig.key === 'pl' && (
															<span className='ms-1'>
																{sortConfig.direction === 'asc'
																	? '↑'
																	: '↓'}
															</span>
														)}
													</th>
													<th
														style={{
															width: '10%',
															borderBottom: '2px solid #dee2e6',
															backgroundColor: '#e3f2fd',
															minWidth: '120px',
															cursor: 'pointer',
														}}
														onClick={() => handleSort('plMedical')}
														className={`text-center fw-bold ${sortConfig.key === 'plMedical' ? 'sortable-header' : ''}`}>
														PL(Medical)
														{sortConfig.key === 'plMedical' && (
															<span className='ms-1'>
																{sortConfig.direction === 'asc'
																	? '↑'
																	: '↓'}
															</span>
														)}
													</th>
													<th
														style={{
															width: '7%',
															borderBottom: '2px solid #dee2e6',
															backgroundColor: '#e3f2fd',
															minWidth: '80px',
															cursor: 'pointer',
														}}
														onClick={() => handleSort('sl')}
														className={`text-center fw-bold ${sortConfig.key === 'sl' ? 'sortable-header' : ''}`}>
														SL
														{sortConfig.key === 'sl' && (
															<span className='ms-1'>
																{sortConfig.direction === 'asc'
																	? '↑'
																	: '↓'}
															</span>
														)}
													</th>
													<th
														style={{
															width: '7%',
															borderBottom: '2px solid #dee2e6',
															backgroundColor: '#fff3e0',
															minWidth: '80px',
															cursor: 'pointer',
														}}
														onClick={() => handleSort('total')}
														className={`text-center fw-bold ${sortConfig.key === 'total' ? 'sortable-header' : ''}`}>
														Total
														{sortConfig.key === 'total' && (
															<span className='ms-1'>
																{sortConfig.direction === 'asc'
																	? '↑'
																	: '↓'}
															</span>
														)}
													</th>
												</tr>
											</thead>
											<tbody>
												{sortedData.map((row: LeaveReportRow) => (
													<tr key={row.sNo}>
														<td
															style={{
																position: 'sticky',
																left: 0,
																backgroundColor: 'white',
																zIndex: 1,
																minWidth: '80px',
															}}>
															{row.sNo}
														</td>
														<td
															style={{
																position: 'sticky',
																left: '80px',
																backgroundColor: 'white',
																zIndex: 1,
																minWidth: '200px',
															}}>
															{row.employeeName}
														</td>
														<td style={{ minWidth: '150px' }}>
															{row.employeeCode}
														</td>
														<td style={{ minWidth: '180px' }}>
															{row.Designation}
														</td>
														<td style={{ minWidth: '180px' }}>
															{row.branchName}
														</td>
														<td
															className='text-center'
															style={{ minWidth: '80px' }}>
															{row.cl}
														</td>
														<td
															className='text-center'
															style={{ minWidth: '80px' }}>
															{row.pl}
														</td>
														<td
															className='text-center'
															style={{ minWidth: '120px' }}>
															{row.plMedical}
														</td>
														<td
															className='text-center'
															style={{ minWidth: '80px' }}>
															{row.sl}
														</td>
														<td
															className='text-center fw-bold'
															style={{ minWidth: '80px' }}>
															{row.total}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default ManagementLeaveReport;
